import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 1500,
  "& .MuiDialog-paper": {
    borderRadius: "20px",
    maxWidth: "400px",
    width: "100%",
    margin: "16px",
    [theme.breakpoints.down("sm")]: {
      margin: "8px",
      maxWidth: "calc(100% - 16px)",
    },
  },
}));

const DialogHeader = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  padding: "16px 20px 0",
});

const CloseButton = styled(IconButton)({
  color: "#666",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const ContentBox = styled(Box)({
  padding: "20px 32px 32px",
  textAlign: "center",
});

const EmailIconContainer = styled(Box)({
  position: "relative",
  display: "inline-block",
  margin: "24px 0",
});

const StyledEmailIcon = styled(EmailIcon)({
  fontSize: "80px",
  color: "#7d3cff",
});

const CheckmarkIcon = styled(CheckCircleIcon)({
  position: "absolute",
  bottom: "8px",
  right: "8px",
  fontSize: "32px",
  color: "#fff",
  backgroundColor: "#7d3cff",
  borderRadius: "50%",
  padding: "4px",
});

const SendButton = styled(Button)({
  backgroundColor: "#7d3cff",
  color: "#fff",
  borderRadius: "12px",
  padding: "14px 32px",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  width: "100%",
  marginTop: "24px",
  "&:hover": {
    backgroundColor: "#5f2bb7",
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    color: "#666",
  },
});

const EmailVerificationDialog = ({ 
  open, 
  onClose, 
  onSend, 
  businessEmail,
  isLoading = false 
}) => {
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        timeout: 300,
      }}
    >
      <DialogHeader>
        <CloseButton onClick={onClose} size="small">
          <CloseIcon />
        </CloseButton>
      </DialogHeader>
      
      <DialogContent sx={{ p: 0 }}>
        <ContentBox>
          {/* Instruction Text */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#000",
              fontSize: "18px",
              lineHeight: 1.4,
              mb: 2,
            }}
          >
            in order to continue you need to
            <br />
            confirm your email address
          </Typography>

          {/* Email Icon with Checkmark */}
          <EmailIconContainer>
            <StyledEmailIcon />
            <CheckmarkIcon />
          </EmailIconContainer>

          {/* Informational Text */}
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              fontSize: "14px",
              lineHeight: 1.4,
              mb: 3,
            }}
          >
            we will send you an email to
            <br />
            verify your account
          </Typography>

          {/* Business Email Display */}
          {businessEmail && (
            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "16px",
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#495057",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                {businessEmail}
              </Typography>
            </Box>
          )}

          {/* Send Button */}
          <SendButton
            onClick={onSend}
            disabled={isLoading}
            variant="contained"
          >
            {isLoading ? "sending..." : "send"}
          </SendButton>
        </ContentBox>
      </DialogContent>
    </StyledDialog>
  );
};

export default EmailVerificationDialog;
